import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '@/lib/api/types';
import type { JournalResponse } from '@/lib/api/types';
import { fetchEvents, fetchMessages, fetchSources } from './api';
import type {
  EventCriteria,
  IntegrationEventInfo,
  IntegrationMessageInfo,
  IntegrationSourceInfo,
  MessageCriteria,
} from './api';

export type MonitorErrorKey =
  | 'monitor.errorForbidden'
  | 'monitor.errorFilter'
  | 'monitor.errorLoad';

const errorKeyFor = (caught: unknown): MonitorErrorKey => {
  const status = caught instanceof ApiError ? caught.status : 0;

  // A 403 will not be fixed by retrying. A 400 is a rejected filter, and this is now
  // the ONLY thing standing between a bad `source` and a broken feed: the filter used
  // to be checked against a hardcoded list of codes, which is what silently dropped a
  // real source the list had never heard of. The message it maps to already said the
  // right thing — a saved link may name a source that no longer exists.
  return status === 403
    ? 'monitor.errorForbidden'
    : status === 400
      ? 'monitor.errorFilter'
      : 'monitor.errorLoad';
};

/* ---- The source codes, for the two filter dropdowns ---------------------- */

/**
 * Fetched once per page load and kept.
 *
 * Which sources EXIST is the server's to say — the list used to be hardcoded here
 * and fell behind it, which is how `NQZ_AMS_FTP` came to be missing. The set changes
 * when a feed is added to the backend, not while a shift is worked, so a request per
 * view switch would be a request for data that never moved.
 *
 * Separate from `useSources` on purpose. That one drives the sources table and wants
 * `health` recomputed on every load; this one wants only the codes, and wants them
 * without a round trip each time somebody switches to Events.
 */
let codeCache: Promise<string[]> | null = null;

const fetchSourceCodes = (): Promise<string[]> => {
  codeCache ??= fetchSources()
    .then((sources) => sources.map((source) => source.sourceCode).filter((code) => code !== ''))
    .catch((caught: unknown) => {
      // Cleared so the next view can try again: a failed list must not be remembered
      // as an empty one for the rest of the session.
      codeCache = null;
      throw caught;
    });

  return codeCache;
};

/**
 * The codes a filter dropdown can offer.
 *
 * A failure is not fatal — the feed still loads unfiltered, the dropdown just has
 * nothing to offer — so it resolves to an empty list rather than throwing.
 */
export const useSourceCodes = (): string[] => {
  const [codes, setCodes] = useState<string[]>([]);

  useEffect(() => {
    let active = true;

    fetchSourceCodes()
      .then((loaded) => {
        if (active) setCodes(loaded);
      })
      .catch(() => {
        if (active) setCodes([]);
      });

    return () => {
      active = false;
    };
  }, []);

  return codes;
};

/* ---- Sources ------------------------------------------------------------- */

interface SourcesOutcome {
  nonce: number;
  sources: IntegrationSourceInfo[];
  errorKey: MonitorErrorKey | null;
}

export interface SourcesState {
  sources: IntegrationSourceInfo[];
  isLoading: boolean;
  errorKey: MonitorErrorKey | null;
  reload: () => void;
  /** Folds the source a PUT just returned back into the list — the response IS the row. */
  apply: (source: IntegrationSourceInfo) => void;
}

/**
 * The source list. One request renders the whole view; `health` is computed by the
 * server per request, so a reload is also how the traffic lights refresh.
 *
 * Loading is derived, not stored — the same pattern as every list hook here.
 */
export const useSources = (): SourcesState => {
  const [nonce, setNonce] = useState(0);
  const [outcome, setOutcome] = useState<SourcesOutcome | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    fetchSources(controller.signal)
      .then((sources) => setOutcome({ nonce, sources, errorKey: null }))
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setOutcome({ nonce, sources: [], errorKey: errorKeyFor(caught) });
      });

    return () => controller.abort();
  }, [nonce]);

  const reload = useCallback(() => setNonce((current) => current + 1), []);

  const apply = useCallback((saved: IntegrationSourceInfo) => {
    setOutcome((current) =>
      current
        ? {
            ...current,
            sources: current.sources.map((source) =>
              source.sourceCode === saved.sourceCode ? saved : source,
            ),
          }
        : current,
    );
  }, []);

  const isCurrent = outcome?.nonce === nonce;

  return {
    sources: outcome?.errorKey ? [] : (outcome?.sources ?? []),
    isLoading: !isCurrent,
    errorKey: isCurrent ? outcome.errorKey : null,
    reload,
    apply,
  };
};

/* ---- The two journals ---------------------------------------------------- */

interface JournalOutcome<T> {
  key: string;
  rows: T[];
  currPage: number;
  total: number;
  /** `rsCount` saturated at 10 000 — render "10 000+" and trust `lastBlock` alone. */
  capped: boolean;
  isLastPage: boolean;
  errorKey: MonitorErrorKey | null;
}

export interface JournalState<T> {
  rows: T[];
  currPage: number;
  total: number;
  capped: boolean;
  isLastPage: boolean;
  isLoading: boolean;
  errorKey: MonitorErrorKey | null;
}

/**
 * Runs one journal query — events and deliveries are the same envelope with
 * different rows, so they share this. Keyed off the serialized criteria, previous
 * rows kept on screen while the next page loads, stale answers discarded: the
 * flight board's pattern, once.
 *
 * The fetcher is a module-level function, so listing it as a dependency is free —
 * it never changes — and the criteria travel as the serialized key, which is the
 * only form of them that is stable across renders.
 */
const useJournal = <C, T>(
  criteria: C,
  fetch: (criteria: C, signal: AbortSignal) => Promise<JournalResponse<T>>,
): JournalState<T> => {
  const key = JSON.stringify(criteria);
  const [outcome, setOutcome] = useState<JournalOutcome<T> | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    fetch(JSON.parse(key) as C, controller.signal)
      .then((response) => {
        setOutcome({
          key,
          rows: Array.isArray(response.rows) ? response.rows : [],
          currPage: response.currPage ?? 1,
          total: response.rsCount ?? 0,
          capped: response.rsCountCapped === true,
          isLastPage: response.lastBlock !== false,
          errorKey: null,
        });
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;

        setOutcome({
          key,
          rows: [],
          currPage: 1,
          total: 0,
          capped: false,
          isLastPage: true,
          errorKey: errorKeyFor(caught),
        });
      });

    return () => controller.abort();
  }, [key, fetch]);

  const isCurrent = outcome?.key === key;

  return {
    rows: outcome?.errorKey ? [] : (outcome?.rows ?? []),
    currPage: outcome?.currPage ?? 1,
    total: outcome?.total ?? 0,
    capped: outcome?.capped ?? false,
    isLastPage: outcome?.isLastPage ?? true,
    isLoading: !isCurrent,
    errorKey: isCurrent ? outcome.errorKey : null,
  };
};

export const useEvents = (criteria: EventCriteria): JournalState<IntegrationEventInfo> =>
  useJournal(criteria, fetchEvents);

export const useDeliveries = (criteria: MessageCriteria): JournalState<IntegrationMessageInfo> =>
  useJournal(criteria, fetchMessages);
